import streamsaver from 'streamsaver';
import { getBearerString } from './jwt_token';
import config from './config';

/**
 * Initiates a streamed file download using streamsaver, with progress reporting.
 * This function bypasses browser memory limitations for large files by streaming directly to disk.
 *
 * @param {string} bucket_name_file_path The full path to the file on the server.
 * @param {(progress: number) => void} [onProgress] An optional callback function to report download progress (0-100).
 * @returns {Promise<void>} A Promise that resolves when the download is complete, or rejects on error.
 */
const downloadFile = (bucket_name_file_path, onProgress) => {
  const encodedPath = encodeURIComponent(bucket_name_file_path);
  const filename = bucket_name_file_path.split('/').pop();
  const urli = `${config.data_platform_api_url}/data/download?bucket_name_file_path=${encodedPath}`;

  // --- Initial Streamsaver and Service Worker Checks ---
  if (!streamsaver.supported) {
    console.error('Download Error: streamsaver is not supported in this browser. Please use Chrome, Firefox, or Edge for streaming downloads.');
    onProgress?.(-1); // Use optional chaining for clarity
    return Promise.reject(new Error('Browser does not support streaming downloads.'));
  }

  if (!navigator.serviceWorker || !navigator.serviceWorker.controller) {
    console.warn('Service Worker Warning: Service Worker is not active or ready. Streaming download might fail or prompt for activation.');
  }
  // --- End Checks ---

  return new Promise(async (resolve, reject) => {
    let writer;
    try {
      const response = await fetch(urli, {
        headers: {
          'Authorization': getBearerString()
        }
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      const contentLength = response.headers.get('Content-Length');
      const totalLength = contentLength ? parseInt(contentLength, 10) : 0;
      let receivedLength = 0;
      let progress = 0;

      const fileStream = streamsaver.createWriteStream(filename, {
        size: totalLength,
      });

      writer = fileStream.getWriter();
      const reader = response.body?.getReader();

      if (!reader) {
        throw new Error("Failed to get a readable stream from the response body.");
      }

      console.log(`Starting download for: ${filename}`);

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('Network stream complete.');
          break;
        }

        await writer.write(value);
        receivedLength += value.length;

        if (totalLength) {
          const currentProgress = Math.round((receivedLength / totalLength) * 100);
          if (currentProgress !== progress) {
            progress = currentProgress;
            onProgress?.(progress);
          }
        }
      }

      console.log('File download initiated successfully.');
      onProgress?.(100);
      resolve();
    } catch (error) {
      console.error('There has been a problem with your fetch operation or streamsaver:', error);
      onProgress?.(-1);
      reject(error);
    } finally {
      if (writer) {
        try {
          await writer.close();
        } catch (closeError) {
          console.warn('Error closing streamsaver writer:', closeError);
        }
      }
    }
  });
};

export const getFileSize = (bucket_name_file_path) => {
  const encodedPath = encodeURIComponent(bucket_name_file_path);
  const url = `${config.data_platform_api_url}/data/filesize?bucket_name_file_path=${encodedPath}`;
  return new Promise((resolve, reject) => {
    fetch(url, {
      headers: {
        'Authorization': getBearerString()
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        resolve(data.file_size);
      })
      .catch(error => {
        console.error('There has been a problem with your fetch operation:', error);
        reject(error);
      });
  });
};

export default downloadFile;